<template>
  <div class="conv-header">
    <div class="conversation-header--details">
      <div class="user">
        <back-button v-if="showBackButton" :back-url="backButtonUrl" />
        <Thumbnail
          :src="currentContact.thumbnail"
          :badge="inboxBadge"
          :username="currentContact.name"
          :status="currentContact.availability_status"
        />
        <div class="user--profile__meta">
          <div class="user--name-wrapper">
            <h3 v-if="!isEditingName" class="sub-block-title user--name text-truncate">
              <span @click="startEditingName">{{ currentContact.name }}</span>
              <fluent-icon
                v-if="!isHMACVerified"
                v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
                size="14"
                class="hmac-warning__icon"
                icon="warning"
              />
            </h3>
            <div v-else class="name-edit-wrapper">
              <input
                ref="nameInput"
                v-model="editedName"
                type="text"
                class="name-input"
                @blur="saveName"
                @keyup.enter="saveName"
                @keyup.esc="cancelEdit"
              />
            </div>
          </div>
          <div class="conversation--header--actions">
            <inbox-name v-if="inbox" :inbox="inbox" class="header-channel-chip" />
            <span v-if="isSnoozed" class="snoozed--display-text">
              {{ snoozedDisplayText }}
            </span>
            <span
              v-if="currentChat.protocol_id"
              class="protocol_Id--display-text margin-right-small"
            >
              #{{ currentChat.protocol_id }}
            </span>
            <span
              v-if="currentContact.whatsapp_valid"
              class="protocol_Id--display-text margin-right-small"
            >
              {{ $t('CONVERSATION.HEADER.VALID_WHATSAPP') }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="header-actions-wrap"
        :class="{ 'has-open-sidebar': isContactPanelOpen }"
      >
        <whatsapp-call-button
          v-if="inbox && currentContact"
          :conversation="currentChat"
          :contact="currentContact"
          :inbox="inbox"
        />
        <more-actions :conversation-id="currentChat.id" />
      </div>
    </div>
  </div>
</template>
<script>
import { hasPressedAltAndOKey } from 'shared/helpers/KeyboardHelpers';
import { mapGetters } from 'vuex';
import agentMixin from '../../../mixins/agentMixin.js';
import BackButton from '../BackButton';
import differenceInHours from 'date-fns/differenceInHours';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import inboxMixin from 'shared/mixins/inboxMixin';
import InboxName from '../InboxName';
import MoreActions from './MoreActions';
import WhatsappCallButton from './WhatsappCallButton';
import Thumbnail from '../Thumbnail';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
export default {
  components: {
    BackButton,
    InboxName,
    MoreActions,
    WhatsappCallButton,
    Thumbnail,
  },
  mixins: [inboxMixin, agentMixin, eventListenerMixins],
  props: {
    chat: {
      type: Object,
      default: () => {},
    },
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isEditingName: false,
      editedName: '',
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      uiFlags: 'inboxAssignableAgents/getUIFlags',
      currentChat: 'getSelectedChat',
    }),
    chatMetadata() {
      return this.chat.meta;
    },
    backButtonUrl() {
      const {
        params: { accountId, inbox_id: inboxId, label, teamId },
        name,
      } = this.$route;
      return conversationListPageURL({
        accountId,
        inboxId,
        label,
        teamId,
        conversationType: name === 'conversation_mentions' ? 'mention' : '',
      });
    },
    isHMACVerified() {
      if (!this.isAWebWidgetInbox) {
        return true;
      }
      return this.chatMetadata.hmac_verified;
    },
    currentContact() {
      if (
        this.chatMetadata?.assignee?.id !== this.currentUser?.id &&
        (this?.chat?.channel === 'Channel::Internal' ||
          this?.chat?.meta?.channel === 'Channel::Internal')
      ) {
        return this.chatMetadata.assignee;
      }

      return this.$store.getters['contacts/getContact'](
        this.chatMetadata.sender.id
      );
    },
    isSnoozed() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    snoozedDisplayText() {
      const { snoozed_until: snoozedUntil } = this.currentChat;
      if (snoozedUntil) {
        // When the snooze is applied, it schedules the unsnooze event to next day/week 9AM.
        // By that logic if the time difference is less than or equal to 24 + 9 hours we can consider it tomorrow.
        const MAX_TIME_DIFFERENCE = 33;
        const snoozedUntilTime = new Date(snoozedUntil).toLocaleString("pt-BR");
        return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL') + snoozedUntilTime;
      }
      return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
    },
    contactPanelToggleText() {
      return `${
        this.isContactPanelOpen
          ? this.$t('CONVERSATION.HEADER.CLOSE')
          : this.$t('CONVERSATION.HEADER.OPEN')
      } ${this.$t('CONVERSATION.HEADER.DETAILS')}`;
    },
    inbox() {
      const { inbox_id: inboxId } = this.chat;
      return this.$store.getters['inboxes/getInbox'](inboxId);
    },
    hasMultipleInboxes() {
      return this.$store.getters['inboxes/getInboxes'].length > 1;
    },
  },

  methods: {
    handleKeyEvents(e) {
      if (hasPressedAltAndOKey(e)) {
        this.$emit('contact-panel-toggle');
      }
    },
    startEditingName() {
      this.isEditingName = true;
      this.editedName = this.currentContact.name;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    cancelEdit() {
      this.isEditingName = false;
      this.editedName = '';
    },
    async saveName() {
      if (!this.editedName.trim() || this.editedName === this.currentContact.name) {
        this.cancelEdit();
        return;
      }
      
      try {
        await this.$store.dispatch('contacts/update', {
          id: this.currentContact.id,
          name: this.editedName.trim(),
        });
        this.isEditingName = false;
      } catch (error) {
        console.error('Error updating contact name:', error);
        this.cancelEdit();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/woot';

.conv-header {
  flex: 0 0 var(--space-jumbo);
  flex-direction: row;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  padding: 0 var(--space-normal);

  @include breakpoint(medium up) {
    flex-direction: column;
  }
}

.conversation-header--details {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @include breakpoint(medium up) {
    flex-direction: row;
    justify-content: space-between;
    gap: var(--space-normal);
  }
}

.user {
  display: flex;
  align-items: center;
  // vence o global `_conv-header.scss` que faz justify center via mixin flex-align
  justify-content: flex-start !important;
  gap: var(--space-small);
  min-width: 0;
  flex: 1;
}

.user--profile__meta {
  min-width: 0;
}

// Chip da caixa de entrada / canal na sub-linha (estilo .chan-chip do protótipo)
::v-deep .header-channel-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-micro);
  background: var(--s-50);
  color: var(--s-600);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  padding: 2px 8px;
  border-radius: 6px;
  margin: 0;
  max-width: 14rem;
  // único item que encolhe quando falta espaço; nome sempre numa linha só
  flex: 0 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .fluent-icon,
  svg,
  i {
    flex: none;
    color: var(--s-500);
  }
}

.option__desc {
  display: flex;
  align-items: center;
}

.option__desc {
  &::v-deep .status-badge {
    margin-right: var(--space-small);
    min-width: 0;
    flex-shrink: 0;
  }
}

.user--name {
  display: inline-block;
  line-height: 1.2;
  text-transform: capitalize;
  margin: 0;
  padding: 0;
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-bold);
  color: var(--color-ink-strong) !important;

  span {
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.user--name-wrapper {
  width: 100%;
}

.name-edit-wrapper {
  width: 100%;
  margin-bottom: var(--space-small);
}

.name-input {
  width: 100%;
  padding: var(--space-small);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-small);
  font-size: var(--font-size-default);
  
  &:focus {
    outline: none;
    border-color: var(--w-500);
  }
}

.conversation--header--actions {
  align-items: center;
  display: flex;
  // tudo em uma única linha; quando falta espaço, só o chip da caixa de
  // entrada encolhe/trunca — os badges (protocolo/WhatsApp) ficam inteiros.
  flex-wrap: nowrap;
  min-width: 0;
  font-size: var(--font-size-mini);
  gap: var(--space-small);
  margin-top: var(--space-micro);
  color: var(--s-500);

  ::v-deep .inbox--name {
    margin: 0;
  }

  .user--profile__button {
    padding: 0;
    color: var(--s-500);
    font-weight: var(--font-weight-medium);
  }

  .snoozed--display-text {
    font-weight: var(--font-weight-medium);
    color: var(--y-600);
  }
}

// Pílula do protocolo/ticket — estilo .proto do protótipo (tint cyan claro + texto cyan escuro)
.protocol_Id--display-text {
  display: inline-flex;
  align-items: center;
  // badge não encolhe nem quebra o texto internamente (evita o corte)
  flex: none;
  white-space: nowrap;
  font-weight: var(--font-weight-bold);
  background-color: var(--color-woot-tint);
  padding: 2px 8px;
  color: var(--color-woot-darker);
  border-radius: 7px;
  font-variant-numeric: tabular-nums;
}

.hmac-warning__icon {
  color: var(--y-600);
  margin: 0 var(--space-micro);
}
</style>
